/// <amd-module name="Wit/Common/WorkItemNodeService/IWorkItemNodeService" />
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { TreeNodeStructureType, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { INode, findByPath } from "Wit/Common/NodeHelpers";
import { IProjectData } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";

export interface IWorkItemNodeService extends IVssService {
    findIterationNodeByPath(projectId: string, path: string): Promise<WorkItemClassificationNode | undefined>;
    getAreaPathNodes(projectId: string): Promise<WorkItemClassificationNode | undefined>;
    getIterationNodes(projectId: string): Promise<WorkItemClassificationNode | undefined>;
    removeIterationNodes(projectId: string): void;
    getMruAreaPaths(projectId: string): Promise<number[]>;
    updateMruAreaPaths(projectId: string, ids: number[]): Promise<number[]>;
    getMruIterations(projectId: string): Promise<number[]>;
    updateMruIterations(projectId: string, ids: number[]): Promise<number[]>;
}

export namespace NodeConstants {
    export const maxNodeDepth = 15;
}

namespace ClassificationFieldsMruSettingKeys {
    export const SettingsKey = "ClassificationFieldsMru";
    export const AreaPathMruKey = "AreaPath";
    export const IterationPathMruKey = "IterationPath";
    export const MruLength = 5;
}

class WorkItemNodeService extends VssService implements IWorkItemNodeService {
    private projectToAreaPathMap = new Map<string, WorkItemClassificationNode>();
    private projectToIterationMap = new Map<string, WorkItemClassificationNode>();
    private projectPromiseMap = new Map<string, Promise<WorkItemClassificationNode[] | null>>();
    private projectToMruAreaPathsMap = new Map<string, number[]>();
    private projectToMruIterationsMap = new Map<string, number[]>();
    private projectMruPromiseMap = new Map<string, Promise<{ [key: string]: any }>>();

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
    }

    public async findIterationNodeByPath(projectId: string, path: string): Promise<WorkItemClassificationNode | undefined> {
        if (!this.projectToIterationMap.get(projectId)) {
            await this.loadNodes(projectId);
        }

        const projectNode = this.projectToIterationMap.get(projectId);
        return findByPath(projectNode as INode, path, undefined, 1) as WorkItemClassificationNode | undefined;
    }

    public async getAreaPathNodes(projectId: string): Promise<WorkItemClassificationNode | undefined> {
        if (!this.projectToAreaPathMap.get(projectId)) {
            await this.loadNodes(projectId);
        }

        return this.projectToAreaPathMap.get(projectId);
    }

    public async getIterationNodes(projectId: string): Promise<WorkItemClassificationNode | undefined> {
        if (!this.projectToIterationMap.get(projectId)) {
            await this.loadNodes(projectId);
        }

        return this.projectToIterationMap.get(projectId);
    }

    public removeIterationNodes(projectId: string): void {
        this.projectToIterationMap.delete(projectId);
        this.projectPromiseMap.delete(projectId);
    }

    public async getMruAreaPaths(projectId: string): Promise<number[]> {
        if (!this.projectToMruAreaPathsMap.get(projectId)) {
            await this.loadMruNodeIds(projectId);
        }

        return this.projectToMruAreaPathsMap.get(projectId)!;
    }

    public async updateMruAreaPaths(projectId: string, ids: number[]): Promise<number[]> {
        let oldIds = this.projectToMruAreaPathsMap.get(projectId);
        if (!oldIds) {
            oldIds = await this.getMruAreaPaths(projectId);
        }

        const newMru = await this.updateMru(projectId, oldIds, ids, ClassificationFieldsMruSettingKeys.AreaPathMruKey);
        this.projectToMruAreaPathsMap.set(projectId, newMru);
        return newMru;
    }

    public async getMruIterations(projectId: string): Promise<number[]> {
        if (!this.projectToMruIterationsMap.get(projectId)) {
            await this.loadMruNodeIds(projectId);
        }

        return this.projectToMruIterationsMap.get(projectId)!;
    }

    public async updateMruIterations(projectId: string, ids: number[]): Promise<number[]> {
        let oldIds = this.projectToMruIterationsMap.get(projectId);
        if (!oldIds) {
            oldIds = await this.getMruIterations(projectId);
        }

        const newMru = await this.updateMru(projectId, oldIds, ids, ClassificationFieldsMruSettingKeys.IterationPathMruKey);
        this.projectToMruIterationsMap.set(projectId, newMru);
        return newMru;
    }

    private async updateMru(projectId: string, oldIds: number[], newIds: number[], settingsKey: string): Promise<number[]> {
        const newMru = this.buildNewMru(oldIds, newIds);
        const key = `${ClassificationFieldsMruSettingKeys.SettingsKey}/${settingsKey}`;
        const settingsEntry = { [key]: newMru };
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        await settingsService.setEntries(settingsEntry, SettingsUserScope.Me, "project", projectId);
        return newMru;
    }

    private async loadMruNodeIds(projectId: string): Promise<void> {
        let mruPromise = this.projectMruPromiseMap.get(projectId);
        if (!mruPromise) {
            const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
            mruPromise = settingsService.getEntriesAsync(ClassificationFieldsMruSettingKeys.SettingsKey, SettingsUserScope.Me, "project", projectId);
        }

        const mruData = await mruPromise;
        if (!mruData) {
            return;
        }

        const areaPathMru = (mruData && mruData[ClassificationFieldsMruSettingKeys.AreaPathMruKey]) || [];
        const iterationPathMru = (mruData && mruData[ClassificationFieldsMruSettingKeys.IterationPathMruKey]) || [];
        this.projectToMruAreaPathsMap.set(projectId, areaPathMru);
        this.projectToMruIterationsMap.set(projectId, iterationPathMru);
    }

    private async loadNodes(projectId: string): Promise<void> {
        let nodesPromise = this.projectPromiseMap.get(projectId);
        const dataManager = this.pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        const projectsPromise = dataManager.beginGetFieldProjectData(projectId);
        if (!nodesPromise) {
            if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseClassificationNodeDataProvider, false)) {
                nodesPromise = dataManager.beginGetNodes(projectId);
            } else {
                const client = getWorkItemTrackingClient(this.pageContext);
                nodesPromise = client.getRootNodes(projectId, /* depth of children to be fetched */ NodeConstants.maxNodeDepth);
            }
            this.projectPromiseMap.set(projectId, nodesPromise);
        }

        const [nodes, projectData] = await Promise.all([nodesPromise, projectsPromise]);
        if (!nodes) {
            return;
        }

        const project = projectData?.projects[0];

        // expect a list of two nodes, one containing area path tree and the other containing iteration tree
        const areaPathRootNode = this.normalizeNode(
            project,
            nodes.find(n => n.structureType === TreeNodeStructureType.Area)
        );
        const iterationRootNode = this.normalizeNode(
            project,
            nodes.find(n => n.structureType === TreeNodeStructureType.Iteration)
        );
        areaPathRootNode && this.projectToAreaPathMap.set(projectId, areaPathRootNode);
        iterationRootNode && this.projectToIterationMap.set(projectId, iterationRootNode);
    }

    /**
     * The WIT rules engine will differentiate between the base project node and the base Area and Iteration nodes for the project,
     * and on the server work items can only be assigned to the base project node.
     * The API returns the Area and Iteration root nodes, {project}/Iteration, instead of the base project node. Since work items assigned
     * the root area path will have the base project node, normalize the nodes so that the root item is the base project node.
     */
    private normalizeNode(project?: IProjectData, root?: WorkItemClassificationNode) {
        if (!project || !root) {
            return root;
        }

        return {
            attributes: {},
            children: root.children,
            hasChildren: root.hasChildren,
            id: project.id,
            identifier: project.guid,
            name: project.name,
            path: project.name,
            structureType: root.structureType
        } as WorkItemClassificationNode;
    }

    private buildNewMru(oldMru: number[], values: number[]) {
        if (!values || values.length === 0) {
            return oldMru;
        }

        const uniqueValues = new Set(values);
        const filteredOldMru = oldMru.filter(id => !uniqueValues.has(id));
        const newMru = [...uniqueValues, ...filteredOldMru];
        newMru.splice(ClassificationFieldsMruSettingKeys.MruLength, newMru.length - ClassificationFieldsMruSettingKeys.MruLength);

        return newMru;
    }
}

const serviceName = "IWorkItemNodeService";
Services.add(serviceName, { serviceFactory: WorkItemNodeService });
